import { toValue } from 'vue';

import type { DefaultError, QueryKey, Updater } from '@tanstack/query-core';
import type {
  UseQueryOptions,
} from '@tanstack/vue-query';
import { getApp } from '@modules/core/app/utils';
import { RequestRetryError, RequestRetryFailureError } from '@modules/core/errors';
import { isInErrorExclusionList, REQUEST_NO_RETRY_ERROR_NAMES_LIST } from '@modules/core/errors/errorExclusions';
import { useToastStore } from '@modules/toast/store';
import * as Sentry from '@sentry/vue';
import {
  useQuery,
  useQueryClient,
} from '@tanstack/vue-query';
import { isFunction, isString } from '@utils/guards';

import type { IContextQueryResult } from './types';
import {
  waitQueryFetched,
  waitQueryLoaded,
} from './utils';

const MAX_RETRY_COUNT = 2;

function captureRetryExceptionToSentry<TQueryKey extends QueryKey = QueryKey>(queryKey: TQueryKey | undefined, isFailure = false): void {
  const queryKeyName = queryKey?.[0];
  if (isString(queryKeyName)) {
    Sentry.withScope((scope) => {
      scope.setLevel('warning');
      scope.setTag('queryKey', queryKeyName);
      Sentry.captureException(isFailure ? new RequestRetryFailureError() : new RequestRetryError());
    });
  }
}

export default function useContextQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey> & { showErrorsInToast?: boolean },
): IContextQueryResult<TQueryFnData, TError, TData> {
  return getApp().runWithContext(() => {
    const queryClient = useQueryClient();
    const optionsUnref = toValue(options);
    const queryKeyUnref = toValue(optionsUnref.queryKey);
    const queryFn = optionsUnref.queryFn;
    let queryIsRetried = false;

    const query = useQuery<TQueryFnData, TError, TData, TQueryKey>({
      retry(failureCount, error) {
        if (isInErrorExclusionList(error, REQUEST_NO_RETRY_ERROR_NAMES_LIST)) {
          return false;
        }

        const { retryToast, failureRetryToast } = useToastStore();
        const shouldRetry = failureCount < MAX_RETRY_COUNT;
        queryIsRetried = shouldRetry;
        if (shouldRetry) {
          retryToast(failureCount, MAX_RETRY_COUNT);
          if (failureCount === 0) {
            captureRetryExceptionToSentry(queryKeyUnref);
          }
        } else {
          failureRetryToast();
          captureRetryExceptionToSentry(queryKeyUnref, true);
        }
        return shouldRetry;
      },
      ...options,
      queryFn: isFunction(queryFn)
        ? (context): TQueryFnData | Promise<TQueryFnData> => {
            const promise = queryFn(context);

            if (promise instanceof Promise) {
              promise.then(
                (response) => {
                  if (queryIsRetried) {
                    const { succeedRetryToast } = useToastStore();
                    queryIsRetried = false;
                    succeedRetryToast();
                  }
                  return response;
                },
                (error) => {
                  if (options.showErrorsInToast && !queryIsRetried) {
                    const { showErrorsWithToast } = useToastStore();
                    showErrorsWithToast(error);
                  }
                  return error;
                },
              );
            }
            return promise;
          }
        : undefined,
    });

    return {
      ...query,
      waitLoaded: () => waitQueryLoaded(query),
      waitFetched: () => waitQueryFetched(query),
      setData: (updater: Updater<TQueryFnData | undefined, TQueryFnData | undefined>): void => {
        if (queryKeyUnref) {
          queryClient.setQueryData<TQueryFnData>(queryKeyUnref, updater);
        }
      },
      /**
       * Legacy alias for backward compatibility.
       *
       * TanStack Query has renamed several fields:
       * - `status: "loading"` → `status: "pending"`
       * - `isLoading` → `isPending`
       * - `isInitialLoading` (deprecated, will be removed) → replaced by `isLoading`
       *
       * To avoid refactoring hundreds of existing usages at once,
       * this wrapper keeps backward-compatible flags:
       *
       * - `isLoading`: internally mapped to `isPending`
       *   (so old code using `.isLoading` keeps working as before).
       *
       * - `isInitialLoading`: internally mapped to the new `isLoading`
       *   (which in v5 is implemented as `isPending && isFetching`).
       *   This ensures legacy code using `isInitialLoading` does not break.
       *
       * - https://tanstack.com/query/latest/docs/framework/vue/guides/migrating-to-v5#status-loading-has-been-changed-to-status-pending-and-isloading-has-been-changed-to-ispending-and-isinitialloading-has-now-been-renamed-to-isloading
       *
       * Please use `isPending` in all **new code**.
       * TODO: This alias will be removed once the migration is complete.
       */
      isLoading: query.isPending,
      isInitialLoading: query.isLoading,
    };
  });
}
